import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { ShieldAlert, Users, CalendarRange, Search } from 'lucide-react';
import DuplicateScannerDialog from '../data-hygiene/DuplicateScannerDialog';

const DataHygieneTab = () => {
  const [scope, setScope] = useState(null); // 'leaves' | 'employees' | null

  return (
    <div className="space-y-4" data-testid="data-hygiene-tab">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldAlert className="h-5 w-5 text-rose-600" />
            Data Hygiene — Duplicate Detection
          </CardTitle>
          <p className="text-xs text-gray-500">
            Scan the HR database for accidentally duplicated records that can throw off leave balances,
            payroll, and reports. The same scans are also one click away from the Leaves and Employees pages.
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <button
              onClick={() => setScope('leaves')}
              data-testid="hygiene-scan-leaves-card"
              className="text-left border border-amber-200 hover:border-amber-400 hover:shadow rounded-xl p-4 transition bg-gradient-to-br from-amber-50/60 to-white"
            >
              <div className="flex items-center gap-2 text-amber-700 mb-1">
                <CalendarRange className="h-5 w-5" />
                <h3 className="font-semibold">Scan leave records</h3>
              </div>
              <p className="text-xs text-gray-600">
                Surfaces <b>exact duplicates</b> (same employee + start + end date) and
                <b> overlapping date ranges</b> for the same employee. Common causes:
                accidental re-entry, calendar imports, or a leave being approved and re-logged manually.
              </p>
              <div className="mt-3 text-[11px] inline-flex items-center gap-1 text-amber-700 font-semibold">
                <Search className="h-3 w-3" /> Run scan →
              </div>
            </button>

            <button
              onClick={() => setScope('employees')}
              data-testid="hygiene-scan-employees-card"
              className="text-left border border-rose-200 hover:border-rose-400 hover:shadow rounded-xl p-4 transition bg-gradient-to-br from-rose-50/60 to-white"
            >
              <div className="flex items-center gap-2 text-rose-700 mb-1">
                <Users className="h-5 w-5" />
                <h3 className="font-semibold">Scan employee database</h3>
              </div>
              <p className="text-xs text-gray-600">
                Cross-checks every active &amp; inactive employee for collisions on
                <b> name</b>, <b>employee code</b>, <b>passport</b>, <b>Emirates ID</b>,
                <b> visa number</b>, and <b>labour card</b>. Helps clean up duplicate records left over
                from manual re-onboarding or re-hires.
              </p>
              <div className="mt-3 text-[11px] inline-flex items-center gap-1 text-rose-700 font-semibold">
                <Search className="h-3 w-3" /> Run scan →
              </div>
            </button>
          </div>

          <div className="mt-4 text-[11px] text-gray-500 leading-relaxed border-t pt-3">
            <p className="font-semibold text-gray-700 mb-1">Auto-prevention on entry</p>
            <p>
              When you add a new <b>Leave</b> or <b>Employee</b> that would collide with an existing record,
              the save is blocked with a confirmation dialog showing the conflicts. Click <b>Cancel</b> to
              re-check your entry, or <b>OK</b> to override and save anyway (useful for re-hires and legitimate
              same-day clock-ins on split shifts).
            </p>
          </div>
        </CardContent>
      </Card>

      <DuplicateScannerDialog open={!!scope} onOpenChange={(o) => { if (!o) setScope(null); }} scope={scope || 'leaves'} />
    </div>
  );
};

export default DataHygieneTab;
